import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  signIn, confirmSignIn, signOut, signUp, confirmSignUp, resendSignUpCode,
  resetPassword, confirmResetPassword, updatePassword,
  getCurrentUser, fetchAuthSession, fetchUserAttributes,
} from 'aws-amplify/auth';
import { API_CONFIG } from '../config';

const AuthContext = createContext(null);

// ---------------------------------------------------------------------------
// Partner-API token cache — module-local, in-memory, per (env, context)
//
// The Dev Portal authenticates to the developer API with a Cognito JWT, but
// to call the partner API (/v1/*) it needs an st_* bearer. We mint one via
// GET /developer/scoped-token?tenant=<env>&context=<ctx>&ttl=900 (15-min
// TTL for live-tenant blast-radius) and cache in memory ONLY — security audit
// guidance forbids persisting partner-API tokens to localStorage.
//
// Cache keyed by `${env}:${context}` — the browser may hold several tokens at
// once: the base default-context token for each env, plus a per-data-context
// token while the scoped-key wizard operates in a selected context (#324 /
// ADR-0049). Distinct keys never share a slot.
//
// Refresh strategy: re-mint when the cached token is within REFRESH_BEFORE_MS
// of expiry. With ttl=900 and REFRESH_BEFORE_MS=60s, that's ~one mint per
// 14 minutes per (env, context) per browser session. Cheap.
//
// Concurrent-mint coalescing (added MR #6.5): multiple concurrent callers for
// the SAME (env, context) during the refresh window share a single in-flight
// Promise instead of each starting their own mint. The previous version did N
// mints for N callers — wasteful at best, mint-quota-exhausting at worst.
// `partnerTokenInFlight` tracks in-flight mints per (env, context) key so a
// caller for one key doesn't block another. The in-flight slot is
// cleared in a finally block so a failed mint can be retried on the next
// call. Pinned by AuthContext.test.js "two concurrent callers coalesce".
//
// Clear-during-mint defense (MR #6.5): a monotonically-incrementing
// `cacheGeneration` counter is captured when a mint starts and re-checked
// before the mint's result is written to the cache. If a logout (or any
// other path through `clearPartnerApiTokenCache`) bumps the generation
// while the mint is in flight, the result is THROWN AWAY rather than
// landing in the cache where the next-logged-in identity could read it.
// Without this guard, the contract "logout clears the partner-API tokens"
// was a lie for the 100-2000ms window between mint start and mint finish.
// Pinned by AuthContext.test.js "cache cleared during in-flight mint".
// ---------------------------------------------------------------------------

// Keyed by `${env}:${context}` — the dev portal mints DISTINCT tokens per
// (tenant, context). Most calls (Keys list, API Logs, users) are tenant-level
// admin ops that don't operate in a data context, so they mint in the base
// `default` context (the owner's wildcard `allowed_actions` apply regardless
// of which context the token is pinned to); the scoped-key wizard operates IN
// a selected DATA context (AccessProfile read/create, scoped-key mint) and
// needs a token confined to THAT context instead.
const partnerTokenCache = {};      // `${env}:${context}` -> token string
const partnerTokenExpiry = {};     // `${env}:${context}` -> epoch ms
const partnerTokenInFlight = {};   // `${env}:${context}` -> in-flight Promise
let cacheGeneration = 0;
const REFRESH_BEFORE_MS = 60_000;
const PARTNER_TOKEN_TTL_SECONDS = 900;

/** The base data context — the default for admin/tenant-level calls (Keys,
 *  API Logs, users) that don't operate in a specific data context. The
 *  reserved `vectros-admin` context is deliberately never targeted here: it's
 *  a non-data-bearing control marker, and the partner API rejects an explicit
 *  mint request for it outright. */
export const DEFAULT_PARTNER_CONTEXT = 'default';

/** The reserved, auto-seeded control-plane context — see the comment above.
 *  Exported so any picker that lets the OWNER choose a data context (the
 *  scoped-key wizard, the Scoped Keys context filter) can exclude it: it's
 *  never a meaningful target, and the partner API rejects an explicit mint
 *  pinned to it outright. */
export const RESERVED_VECTROS_ADMIN_CONTEXT = 'vectros-admin';

/**
 * Get a partner-API st_* bearer token for the given env, minting a fresh one
 * if the cached value is absent or near expiry. Returns the token string —
 * callers (the partnerApi.js axios interceptor) attach it as
 * `Authorization: Bearer <token>`.
 *
 * Exported as a plain function (not a hook) so non-React code paths (the
 * axios interceptor) can call it without React context plumbing.
 *
 * Concurrent callers for the SAME (env, context) share one in-flight mint
 * Promise. See the module-level comment above for the rationale.
 *
 * @param {'live'|'test'} env which Vectros tenant to scope the token to
 * @param {string} [context] the AppContext to confine the token to. Defaults to
 *   the base {@link DEFAULT_PARTNER_CONTEXT} (`default`) for tenant-level admin
 *   calls; the scoped-key wizard passes the SELECTED data context so its
 *   AccessProfile + scoped-key operations are authorized there (#324 / ADR-0049).
 * @returns {Promise<string>} the raw st_* token
 * @throws if the developer isn't authenticated or the mint endpoint fails
 */
export function getPartnerApiToken(env = 'test', context = DEFAULT_PARTNER_CONTEXT) {
  if (env !== 'live' && env !== 'test') {
    return Promise.reject(
      new Error(`getPartnerApiToken: env must be 'live' or 'test', got '${env}'`),
    );
  }
  if (!context || typeof context !== 'string') {
    return Promise.reject(
      new Error(`getPartnerApiToken: context must be a non-empty string, got '${context}'`),
    );
  }

  const key = `${env}:${context}`;
  const now = Date.now();
  if (partnerTokenCache[key] && partnerTokenExpiry[key] > now + REFRESH_BEFORE_MS) {
    return Promise.resolve(partnerTokenCache[key]);
  }

  // If a mint for this (env, context) is already running, join it instead of
  // starting a second one. The synchronous assignment below (before the IIFE
  // yields at its first await) ensures that a second caller arriving in the
  // same tick sees the slot populated.
  if (partnerTokenInFlight[key]) {
    return partnerTokenInFlight[key];
  }

  // Capture the cache generation BEFORE the mint starts. If a clear bumps
  // the generation while the mint is in flight, we discard the result —
  // see the module-level comment for the threat model.
  const generationAtStart = cacheGeneration;

  const mintPromise = (async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();
      if (!idToken) throw new Error('Not authenticated — cannot mint partner-API token');

      // The mint confines the token to `context`. Tenant-level admin calls
      // pass the default `default` context (auto-seeded; the owner's wildcard
      // actions apply regardless of context); the scoped-key wizard passes
      // the selected DATA context so its profile + key-mint calls are
      // authorized there instead (#324 / ADR-0049).
      const url = `${API_CONFIG.developerApiBase}/developer/scoped-token`
                + `?tenant=${encodeURIComponent(env)}&ttl=${PARTNER_TOKEN_TTL_SECONDS}`
                + `&context=${encodeURIComponent(context)}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        const err = new Error(`Partner-API token mint failed: ${resp.status} ${body}`);
        // Carry the STATUS as a field, not only inside the message. The caller
        // (partnerApi.attachPartnerToken) has to tell an authentication failure
        // apart from a refusal, and matching on message text to do it is the
        // fragile version of this.
        err.status = resp.status;
        throw err;
      }
      const { token, expiresAt } = await resp.json();

      // If clearPartnerApiTokenCache fired while this mint was in flight,
      // the user this token was minted for no longer has an active session.
      // Throw away the result rather than writing it to the cache where the
      // next-logged-in user could read it. The CALLER receives a rejection;
      // for the original (now-logged-out) caller that's fine — they're no
      // longer waiting on a response.
      if (cacheGeneration !== generationAtStart) {
        throw new Error('Session cleared during partner-API token mint; discarding result.');
      }
      partnerTokenCache[key] = token;
      partnerTokenExpiry[key] = expiresAt * 1000; // backend returns epoch seconds
      return token;
    } finally {
      // Release the in-flight slot — but only if it still references THIS
      // mint. clearPartnerApiTokenCache nulls the slot directly, and a
      // subsequent caller (now in a fresh session) may have already started
      // a new mint and re-populated the slot. Don't clobber theirs.
      if (partnerTokenInFlight[key] === mintPromise) {
        partnerTokenInFlight[key] = null;
      }
    }
  })();
  partnerTokenInFlight[key] = mintPromise;
  return mintPromise;
}

/**
 * Clear cached partner-API tokens, any in-flight mints, AND advance the
 * cache generation so any mint that started before this call discards its
 * result instead of writing it to the cache.
 *
 * Without the generation bump, the contract "logout clears the partner-API
 * tokens" was a lie for the time window between mint start and mint finish:
 *   1. User A's getPartnerApiToken in flight.
 *   2. User A logs out → clearPartnerApiTokenCache nulls everything.
 *   3. Mint resolves; writes User A's token to the cache.
 *   4. User B logs in; first getPartnerApiToken call reads User A's token
 *      from the cache and uses it as User B's bearer.
 * That's a real-world cross-identity token leak. The generation counter
 * guards step 3 — the mint's try block re-checks generation before writing
 * to the cache and throws if it changed.
 */
export function clearPartnerApiTokenCache() {
  cacheGeneration += 1;
  // Drop EVERY (env, context) entry — logout must leave no token of any context
  // readable by the next identity (the cross-identity-leak threat above applies
  // per-context too). The generation bump additionally discards any in-flight mint.
  for (const k of Object.keys(partnerTokenCache)) delete partnerTokenCache[k];
  for (const k of Object.keys(partnerTokenExpiry)) delete partnerTokenExpiry[k];
  for (const k of Object.keys(partnerTokenInFlight)) delete partnerTokenInFlight[k];
}

/**
 * Whether a signIn failure reflects a LINGERING LOCAL SESSION rather than bad
 * credentials. Two cases wedge a fresh login until the stale session is cleared:
 *   - Amplify still considers a user signed in locally
 *     (`UserAlreadyAuthenticatedException`); or
 *   - the stored token was revoked out-of-band — a global sign-out in a SIBLING
 *     app (Vectros Admin, app.vectros.ai) on the SHARED DeveloperUserPool revokes
 *     the refresh token pool-wide, so this browser's cached session is dead but
 *     still present.
 * The already-authenticated case is identified by error name; the revoked case
 * shares `NotAuthorizedException` with bad credentials, so it's distinguished by
 * message. Keying on Amplify's English string is intentional: if that string
 * ever changes the self-heal simply stops engaging (login wedges as before) — it
 * can never misclassify bad credentials as a revoked session. Mirrors the shared
 * @vectros-ai/react CognitoAuthProvider self-heal so the portal can't wedge a
 * login the reference apps recover from.
 */
function isLingeringSessionError(e) {
  if (!e || typeof e.name !== 'string') return false;
  if (e.name === 'UserAlreadyAuthenticatedException') return true;
  return e.name === 'NotAuthorizedException' && /revoked/i.test(e.message || '');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // { email, sub, liveTenantId, testTenantId, partnerId }
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const attrs = await fetchUserAttributes();
      setUser({
        email: attrs.email,
        sub: attrs.sub,
        uuid: attrs['custom:uuid'] || null,
        liveTenantId: attrs['custom:live_tenant_id'] || null,
        testTenantId: attrs['custom:test_tenant_id'] || null,
        partnerId: attrs['custom:partner_id'] || null,
        firstName: attrs.given_name || '',
        lastName: attrs.family_name || '',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then(() => loadUser())
      .catch(() => setLoading(false));
  }, [loadUser]);

  const login = async (email, password) => {
    const credentials = { username: email.toLowerCase(), password };
    let nextStep;
    try {
      ({ nextStep } = await signIn(credentials));
    } catch (e) {
      // A lingering/revoked local session wedges a fresh login (see
      // isLingeringSessionError). Clear it and retry ONCE so the user can always
      // sign back in rather than getting stuck; any other error (bad credentials,
      // unconfirmed user, MFA setup, …) propagates unchanged to the caller.
      if (!isLingeringSessionError(e)) throw e;
      await signOut({ global: true }).catch(() => undefined); // best-effort clear
      clearPartnerApiTokenCache();
      ({ nextStep } = await signIn(credentials));
    }
    if (nextStep.signInStep === 'DONE') {
      await loadUser();
    }
    // Return step so callers can handle MFA or other challenges
    return nextStep.signInStep;
  };

  // Complete an MFA code challenge (TOTP / SMS / EMAIL). Cognito MFA preference
  // is a property of the shared DeveloperUserPool user, so a developer who
  // enrolls TOTP in the Vectros Admin app is challenged HERE too — this portal
  // must be able to answer it (admin-app MR #7.97 §5). Returns the next step;
  // loads the user on DONE.
  const confirmMfa = async (code) => {
    const { nextStep } = await confirmSignIn({ challengeResponse: code });
    if (nextStep.signInStep === 'DONE') {
      await loadUser();
    }
    return nextStep.signInStep;
  };

  const register = async ({ email, password, firstName, lastName, captchaToken }) => {
    const uuid        = crypto.randomUUID();
    const partnerId   = crypto.randomUUID();
    const liveTenantId = crypto.randomUUID();
    const testTenantId = crypto.randomUUID();

    await signUp({
      username: email.toLowerCase(),
      password,
      options: {
        userAttributes: {
          email: email.toLowerCase(),
          given_name: firstName,
          family_name: lastName,
          'custom:type':           'developer',
          'custom:uuid':           uuid,
          'custom:partner_id':     partnerId,
          'custom:live_tenant_id': liveTenantId,
          'custom:test_tenant_id': testTenantId,
        },
        // Anti-bot challenge token (Cloudflare Turnstile — ADR-0021). Cognito
        // forwards clientMetadata to the PreSignup trigger as
        // event.request.clientMetadata, where it is verified server-side. Only
        // sent when a token was produced (widget configured + solved); the
        // backend CAPTCHA_MODE gate decides whether a missing token is allowed.
        ...(captchaToken ? { clientMetadata: { captchaToken } } : {}),
      },
    });
  };

  const confirmEmail = async (email, code) => {
    await confirmSignUp({ username: email.toLowerCase(), confirmationCode: code });
  };

  const resendCode = async (email) => {
    await resendSignUpCode({ username: email.toLowerCase() });
  };

  const forgotPassword = async (email) => {
    await resetPassword({ username: email.toLowerCase() });
  };

  const confirmForgotPassword = async (email, code, newPassword) => {
    await confirmResetPassword({
      username: email.toLowerCase(),
      confirmationCode: code,
      newPassword,
    });
  };

  const changePassword = async (oldPassword, newPassword) => {
    await updatePassword({ oldPassword, newPassword });
  };

  const logout = async () => {
    await signOut({ global: true });
    clearPartnerApiTokenCache();
    setUser(null);
  };

  const getIdToken = async () => {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString() || null;
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, confirmMfa, register, confirmEmail, resendCode,
      forgotPassword, confirmForgotPassword, changePassword,
      logout, getIdToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
